import { useEffect, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { RealtimePostgresChangesPayload } from '@supabase/supabase-js';
import { useOrganization } from '@/hooks/useOrganization';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { externalOrdersService } from '@/services/externalOrders/externalOrdersService';
import { queryKeys } from '@/lib/queryKeys';
import { useRealTimeSubscription } from '@/hooks/useRealTimeSubscription';
import { FEATURE_FLAGS } from '@/types/featureFlags';
import type { ExternalTabRealtimeRow } from '@/constants/externalOrders';
import { Tab } from '@/types';
import { playOrderNotification } from '@/utils/playOrderNotification';

export const NEW_EXTERNAL_ORDER_EVENT = 'new-external-order';

export const useExternalOrders = () => {
  const { currentOrganization } = useOrganization();
  const { isEnabled } = useFeatureFlag();
  const client = useQueryClient();
  const prevPendingCountRef = useRef(0);

  const orgId = currentOrganization?.id ?? '';
  const isFeatureEnabled = isEnabled(FEATURE_FLAGS.EXTERNAL_ORDERS);

  const queryKey = queryKeys.externalOrders(orgId);

  const { data: orders = [], isLoading } = useQuery({
    queryKey,
    queryFn: () => externalOrdersService.getExternalOrders(orgId),
    enabled: isFeatureEnabled && !!orgId,
    staleTime: 30_000,
  });

  const refreshOrders = () =>
    client.fetchQuery({
      queryKey,
      queryFn: () => externalOrdersService.getExternalOrders(orgId),
      staleTime: 0,
    });

  const realtimeListeners = useMemo(
    () => [
      {
        config: {
          event: '*' as const,
          schema: 'public',
          table: 'tabs',
          filter: `organization_id=eq.${orgId},source_type=eq.external`,
        },
        onPayload: (payload: RealtimePostgresChangesPayload<ExternalTabRealtimeRow>) => {
          const tab = payload.new as ExternalTabRealtimeRow | undefined;
          const isNewPending =
            payload.eventType === 'INSERT' &&
            tab?.source_type === 'external' &&
            tab?.external_order_status === 'pending';

          if (isNewPending) {
            void playOrderNotification();
          }

          void (async () => {
            await client.fetchQuery({
              queryKey,
              queryFn: () => externalOrdersService.getExternalOrders(orgId),
              staleTime: 0,
            });

            if (!isNewPending) return;

            const orderId = tab?.id;
            const current = client.getQueryData<Tab[]>(queryKey) ?? [];
            if (orderId && !current.some((order) => order.id === orderId)) {
              await new Promise((resolve) => setTimeout(resolve, 400));
              await client.fetchQuery({
                queryKey,
                queryFn: () => externalOrdersService.getExternalOrders(orgId),
                staleTime: 0,
              });
            }

            window.dispatchEvent(new CustomEvent(NEW_EXTERNAL_ORDER_EVENT));
          })();
        },
      },
    ],
    [orgId, client, queryKey],
  );

  useRealTimeSubscription({
    channelName: `external-orders-${orgId}`,
    orgId: isFeatureEnabled ? orgId : undefined,
    queryClient: client,
    listeners: isFeatureEnabled ? realtimeListeners : [],
  });

  const pendingOrders = useMemo(
    () => orders.filter((order) => order.externalOrderStatus === 'pending'),
    [orders],
  );

  const activeOrders = useMemo(
    () =>
      orders.filter(
        (order) =>
          order.externalOrderStatus &&
          ['accepted', 'preparing', 'ready'].includes(order.externalOrderStatus),
      ),
    [orders],
  );

  useEffect(() => {
    if (!isFeatureEnabled) return;

    const pendingCount = pendingOrders.length;
    if (pendingCount > prevPendingCountRef.current) {
      window.dispatchEvent(new CustomEvent(NEW_EXTERNAL_ORDER_EVENT));
    }
    prevPendingCountRef.current = pendingCount;
  }, [pendingOrders.length, isFeatureEnabled]);

  return {
    orders,
    isLoading,
    isFeatureEnabled,
    pendingOrders,
    activeOrders,
    pendingCount: pendingOrders.length,
    activeCount: activeOrders.length,
    refreshOrders,
  };
};
